<template>
  <b-field
    :horizontal="horizontal"
    :label="label"
    :type="type"
    :custom-class="customClass"
    :message="message"
    v-show="!isHidden"
  >
    <template #label>
      <slot name="label">{{ label }}</slot>
    </template>
    <b-select
      :name="name"
      v-model="value_"
      :expanded="expanded"
      :multiple="multiple"
      :size="`${small ? 'is-small' : ''}`"
      :disabled="
        loading ||
          loading_ ||
          disabled ||
          (disabledOnEmpty ? options.length === 0 : false)
      "
      :loading="loading || loading_"
      @input="onInput"
    >
      <option value v-if="showDefaultOption && !multiple">
        <slot v-if="showDefaultOptionLabel" name="defaultOptionLabel">{{ defaultOptionLabel }}</slot>
      </option>
      <template v-if="grouped">
        <optgroup
          v-for="[key, value] in Object.entries(optionsGrouped)"
          :label="key"
          :key="`group-${key}`"
        >
          <option
            v-for="(option, index) in value"
            :value="getOptionValue(option)"
            :key="getOptionKey(option, index)"
          >
            <slot name="customLabel" :option="option">
              {{ getOptionLabel(option) }}
            </slot>
          </option>
        </optgroup>
      </template>
      <template v-else>
        <option
          v-for="(option, index) in options"
          :value="getOptionValue(option)"
          :key="getOptionKey(option, index)"
        >
          <slot name="customLabel" :option="option">
            {{ getOptionLabel(option) }}
          </slot>
        </option>
      </template>
    </b-select>
    <slot name="control" :value="value_" />
  </b-field>
</template>

<script>
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer.js";

export default {
  props: {
    service: {
      type: Function,
      required: true
    },
    fetchFunc: {
      type: String,
      required: true
    },
    fetchParams: {
      type: Object | String,
      default: () => {}
    },
    responseHandler : {
      type: Function,
      required: false,
    },
    denormalize: {
      type: Boolean,
      default: true
    },
    objectOptions: {
      type: Boolean,
      default: true
    },
    label: {
      type: String
    },
    value: {
      type: String | Number | Array
    },
    valueKey: {
      type: String,
      default: "id"
    },
    labelKey: {
      type: String,
      default: "name"
    },
    horizontal: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default: true,
    },
    prepend: {
      type: Array,
      default: () => []
    },
    fetchOnInit: {
      type: Boolean,
      default: true
    },
    showDefaultOption: {
      type: Boolean,
      default: true
    },
    showDefaultOptionLabel: {
      type: Boolean,
      default: true
    },
    defaultOptionLabel: {
      type: String,
      default: "Please Select"
    },
    customFilter: {},
    // Disabled if options are empty
    disabledOnEmpty: {
      type: Boolean,
      default: false
    },
    // Hidden if options are empty
    hiddenOnEmpty: {
      type: Boolean,
      default: false
    },
    name: {
      type: String
    },
    loading: {
      type: Boolean,
      default: false
    },
    type: {
      type: String
    },
    message: {
      type: String
    },
    customClass: {
      type: String
    },
    // Preselect if only one option
    preselectOnOne: {
      type: Boolean,
      default: false
    },
    hiddenOnOne: {
      type: Boolean,
      default: false
    },
    grouped: {
      type: Boolean,
      default: false,
    },
    groupBy: {
      type: String
    },
    /**
     * [groupBy.value]: label
     */
    groupLabels: {
      type: Object,
    },
  },
  data: () => ({
    value_: "",
    options: [],
    optionsGrouped: {},
    loading_: false
  }),
  computed: {
    isHidden() {
      if (this.hiddenOnEmpty) {
        return ( ! this.loading && ! this.loading_ && this.options.length === 0);
      }
      if (this.hiddenOnOne) {
        return ( ! this.loading && ! this.loading_ && this.options.length === 1);
      }
      return false;
    }
  },
  created() {
    if (this.fetchOnInit) {
      this.fetchOptions();
    }
  },
  watch: {
    value: {
      handler(value) {
        this.value_ = value || (this.multiple ? [] : "");
      },
      immediate: true
    },
    value_(value) {
      this.$emit("input", value);
      this.$emit("update:value", value);

      if (!value) {
        this.$emit("change", null);
        return;
      }

      const found = this.options.find(
        option => option[this.valueKey] === value
      );

      if (found) {
        this.$emit("change", found);
      }
    },
    options(options) {
      this.$emit('options-updated', { options });
    }
  },
  methods: {
    getObjectValue(obj, path) {
      return path.split(".").reduce((acc, val) => {
        acc = acc[val] || "N/A";
        return acc;
      }, obj);
    },
    async fetchOptions() {
      let options = [];

      this.loading_ = true;

      if (typeof this.responseHandler === 'function') {
        try {
          options = await this.service[this.fetchFunc](this.fetchParams).then(
            response => this.responseHandler(response)
          );
        } catch (err) {}
      }
      else {
        try {
          options = await this.service[this.fetchFunc](this.fetchParams).then(
            response =>
              this.denormalize ? JsonDenormalizer.denormalize(response) : response
          );
        } catch (err) {}
      }

      // Prepend additional options via passed props
      options = this.prependOptions(options);
      // Remove duplicate options
      options = this.removeDuplicates(options);

      // Filter options via passed custom filter prop
      if (this.customFilter) {
        options = this.customFilter(options);
      }

      this.options = options;

      if (this.grouped && this.groupBy) {
        let optionsGrouped = options.reduce((acc, v) => {
          let key = this.groupBy;
          let value = v[this.groupBy];
          if (this.groupLabels) {
            if (this.groupLabels[value]) {
              key = this.groupLabels[value];
            } else if (this.groupLabels["default"]) {
              key = this.groupLabels["default"];
            }
          };
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(v);
          return acc;
        }, {});
        // Sort
        let groups = Object.values(this.groupLabels);
        if (!this.groupLabels["default"]) groups.unshift(this.groupBy);
        let optionsGroupedFinal = {};
        for (let group of groups) {
          if (optionsGrouped[group] && optionsGrouped[group].length) {
            optionsGroupedFinal[group] = optionsGrouped[group];
          }
        }
        this.optionsGrouped = optionsGroupedFinal;
      }

      this.checkIfValueExists();

      if (this.preselectOnOne && !this.disabled) {
        if (this.options.length === 1) {
          const isEmpty = this.multiple
            ? this.value.length === 0
            : !this.value_;

          if (isEmpty) {
            this.value_ = this.options[0][this.valueKey];
          }
        }
      }
      this.loading_ = false;
      this.$emit("fetch", this.options);
      return this.options;
    },
    clearOptions() {
      this.reset();
      this.options = [];
      this.optionsGrouped = {};
    },
    reset() {
      this.value_ = this.multiple ? [] : "";
    },
    selectFirstOption() {
      this.value_ = this.options.length ? this.getOptionValue(this.options[0]) : "";
    },
    prependOptions(options) {
      return [...this.prepend, ...options];
    },
    removeDuplicates(options) {
      return options.reduce((acc, option) => {
        if (
          !acc.find(
            item => this.getOptionValue(item) === this.getOptionValue(option)
          )
        ) {
          acc.push(option);
        }
        return acc;
      }, []);
    },
    /**
     * Reset value when not present in options
     */
    checkIfValueExists() {
      if (!this.options.find(option => option[this.valueKey] == this.value)) {
        this.reset();
      }
    },
    getOptionValue(option) {
      return this.objectOptions
        ? this.getObjectValue(option, this.valueKey)
        : option;
    },
    getOptionKey(option, index) {
      const uuid = Math.random()
        .toString(36)
        .substring(2);
      const key = this.objectOptions ? this.getOptionValue(option) : index;
      return `${uuid}-${key}`;
    },
    getOptionLabel(option) {
      return this.objectOptions
        ? this.getObjectValue(option, this.labelKey)
        : option;
    },
    onInput(value) {
      this.$emit("input:manual", value);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/partials/BaseSelect.vue